import type * as React from 'react';

import type { Organization } from '@gitbook/api-client';
import { BadgeLabel } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { Button, LoadingIndicatorIcon, Switch, Tooltip } from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { useUpgrade } from '../../state';
import { useConfigureAISearch } from '../AISearch';

/**
 * Footer of the search modal presenting the AI search feature.
 */
export function AppSearchLensFooter(props: {
    organization: Organization;
}): React.ReactElement | null {
    const { organization } = props;

    const upgrade = useUpgrade('ai-lens');
    const canEnableAI = organization.permissions.admin;
    const { ready, enabled, enable } = useConfigureAISearch(organization);
    const [loading, wrapLoading] = useLoadingState();

    if (!upgrade && ready) {
        // If there is no need to upgrade, the feature is already enabled, and the initial indexation is passed, we don't show the footer
        return null;
    }

    return (
        <div className="flex items-center bg-muted px-6 py-4">
            <Icon.SearchOrAsk size={18} className="text-light" />
            <div className="mx-4 mr-auto items-center">
                Find an answer using <strong>GitBook AI</strong>
            </div>
            <div>
                {upgrade ? (
                    <Button size="medium" {...upgrade} />
                ) : (
                    <>
                        {enabled ? (
                            <BadgeLabel
                                size="medium"
                                kind="info"
                                icon={LoadingIndicatorIcon}
                                tooltip={
                                    <>
                                        The contents are being indexed. It can take a few minutes to
                                        index all.
                                    </>
                                }
                            >
                                Indexing...
                            </BadgeLabel>
                        ) : (
                            <Tooltip
                                label={
                                    canEnableAI
                                        ? 'Enable it in your organization.\nYou can disable it at any time.'
                                        : 'Ask an administrator to enable it in this organization'
                                }
                            >
                                <div>
                                    {canEnableAI ? (
                                        <Switch
                                            value={false}
                                            loading={loading}
                                            onValueChange={() => {
                                                wrapLoading(async () => {
                                                    await enable('search');
                                                });
                                            }}
                                        />
                                    ) : (
                                        <Icon.HelpCircle size={18} className="text-muted" />
                                    )}
                                </div>
                            </Tooltip>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
